import React, { useEffect, useMemo, useRef } from 'react';
import { Brain } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isPrerender } from '../utils/isPrerender';
import { createPerformanceMonitor } from '../utils/performanceMonitor';

interface Argument {
  id: number;
  x: number;
  y: number;
  type: 'plus' | 'minus';
  opacity: number;
  scale: number;
  rotation: number;
  rotationSpeed: number;
  text?: string;
  // Движение для роения
  velocityX: number;
  velocityY: number;
  targetX?: number;
  targetY?: number;
  // Кэш раскладки текста
  lines: string[];
  cardWidth: number;
  cardHeight: number;
  fontSize: number;
  lineHeight: number;
  padding: number;
  layoutBaseSize: number;
}

interface AnimatedBrainProps {
  size?: number;
  className?: string;
  containerClassName?: string;
}

const AnimatedBrain: React.FC<AnimatedBrainProps> = ({ 
  size = 400, 
  className = '',
  containerClassName = ''
}) => {
  const { t } = useTranslation('landing');
  const argumentPool = useMemo(() => ({
    plus: [
      t('brainCardPlus1'), t('brainCardPlus2'), t('brainCardPlus3'), t('brainCardPlus4'),
      t('brainCardPlus5'), t('brainCardPlus6'), t('brainCardPlus7'), t('brainCardPlus8'),
      t('brainCardPlus9'), t('brainCardPlus10'), t('brainCardPlus11'), t('brainCardPlus12')
    ],
    minus: [
      t('brainCardMinus1'), t('brainCardMinus2'), t('brainCardMinus3'), t('brainCardMinus4'),
      t('brainCardMinus5'), t('brainCardMinus6'), t('brainCardMinus7'), t('brainCardMinus8'),
      t('brainCardMinus9'), t('brainCardMinus10'), t('brainCardMinus11'), t('brainCardMinus12')
    ]
  }), [t]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const animationRef = useRef<number>();
  const argumentsRef = useRef<Argument[]>([]);
  const fillProgressRef = useRef<number>(0);
  const isAnimatingRef = useRef<boolean>(false);
  const isPerformanceDisabledRef = useRef<boolean>(false); // Флаг отключения из-за низкой производительности
  const performanceMonitorRef = useRef<ReturnType<typeof createPerformanceMonitor> | null>(null);

  useEffect(() => {
    // Отключаем анимацию для Prerender
    if (isPrerender()) {
      return;
    }
    
    // Проверяем URL‑параметры
    const urlParams = typeof window !== 'undefined' 
      ? new URLSearchParams(window.location.search)
      : null;
    // Принудительный статичный режим (без анимации, только финальное состояние)
    const forceStaticMode =
      urlParams?.has('staticBrain') ||
      urlParams?.get('staticBrain') === '1' ||
      urlParams?.get('staticBrain') === 'true';
    // Режим логирования FPS в консоль (для отладки). Пример: ?brainFpsDebug или ?brainFpsDebug=1
    const debugFpsMode =
      urlParams?.has('brainFpsDebug') ||
      urlParams?.get('brainFpsDebug') === '1' ||
      urlParams?.get('brainFpsDebug') === 'true';
    
    // Отладочное логирование
    if (forceStaticMode) {
      console.log('[AnimatedBrain] Принудительный статичный режим включен через URL параметр staticBrain');
    }
    if (debugFpsMode) {
      console.log('[AnimatedBrain] Включён режим логирования FPS (brainFpsDebug)');
    }
    
    const container = containerRef.current;
    const ensureCanvas = () => {
      let current = canvasRef.current as (HTMLCanvasElement & { __offscreenTransferred?: boolean }) | null;
      if (!current) {
        return null;
      }

      if (current.__offscreenTransferred) {
        const newCanvas = current.cloneNode(false) as HTMLCanvasElement & { __offscreenTransferred?: boolean };
        if (current.parentNode) {
          current.parentNode.replaceChild(newCanvas, current);
        }
        canvasRef.current = newCanvas;
        return newCanvas;
      }

      return current;
    };

    const canvas = ensureCanvas();
    if (!container || !canvas) return;

    // Если принудительно включен статичный режим, пропускаем Worker
    // (проверка будет после определения всех функций, но нужно пропустить Worker)
    const supportsOffscreenCanvas = !forceStaticMode && typeof window !== 'undefined'
      && 'Worker' in window
      && 'OffscreenCanvas' in window
      && typeof (canvas as HTMLCanvasElement & { transferControlToOffscreen?: () => OffscreenCanvas }).transferControlToOffscreen === 'function';

    if (supportsOffscreenCanvas && canvas.transferControlToOffscreen) {
      const worker = new Worker(
        new URL('../workers/animatedBrainWorker.ts', import.meta.url),
        { type: 'module' }
      );
      workerRef.current = worker;

      // При включённом brainFpsDebug:
      // 1) Передаём конфиг для логирования FPS из воркера
      // 2) Подписываемся на сообщения с FPS
      if (debugFpsMode) {
        worker.postMessage({
          type: 'config',
          debugFps: true
        });

        worker.addEventListener('message', (event: MessageEvent<any>) => {
          const data = event.data;
          if (data && data.type === 'fps') {
            const { fps, avgFps, samples } = data;
            console.log(
              `[AnimatedBrain/worker] FPS: ${fps.toFixed(1)}, avg: ${avgFps.toFixed(1)}, samples: ${samples}`
            );
          }
        });
      } else {
        // Явно выключаем debugFps в воркере, если режим не активен
        worker.postMessage({
          type: 'config',
          debugFps: false
        });
      }

      const offscreen = canvas.transferControlToOffscreen();
      (canvas as HTMLCanvasElement & { __offscreenTransferred?: boolean }).__offscreenTransferred = true;
      const getSize = () => {
        const rect = container.getBoundingClientRect();
        return {
          width: rect.width || size,
          height: rect.height || size
        };
      };

      const sendResize = () => {
        const { width, height } = getSize();
        worker.postMessage({
          type: 'resize',
          width,
          height,
          devicePixelRatio: window.devicePixelRatio || 1
        });
      };

      const initialSize = getSize();
      worker.postMessage({
        type: 'init',
        canvas: offscreen,
        width: initialSize.width,
        height: initialSize.height,
        devicePixelRatio: window.devicePixelRatio || 1,
        argumentPool
      }, [offscreen]);

      const intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          worker.postMessage({ type: entry.isIntersecting ? 'start' : 'stop' });
        });
      }, { threshold: 0 });

      intersectionObserver.observe(container);

      const resizeObserver = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(sendResize)
        : null;

      if (resizeObserver) {
        resizeObserver.observe(container);
      }

      const handleWindowResize = () => sendResize();
      window.addEventListener('resize', handleWindowResize);
      sendResize();

      const rect = container.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        worker.postMessage({ type: 'start' });
      }

      return () => {
        intersectionObserver.disconnect();
        resizeObserver?.disconnect();
        window.removeEventListener('resize', handleWindowResize);
        worker.postMessage({ type: 'dispose' });
        worker.terminate();
        workerRef.current = null;
      };
    }

    // Если принудительно включен статичный режим, пропускаем Worker и обычную анимацию
    // (проверка будет после определения всех функций)
    
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // Устанавливаем размер canvas на основе контейнера
    let displayWidth = size;
    let displayHeight = size;
    
    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      displayWidth = rect.width || size;
      displayHeight = rect.height || size;
      
      canvas.width = displayWidth * dpr;
      canvas.height = displayHeight * dpr;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();

    // Подготавливаем список всех аргументов (из переведённого пула)
    const allArguments: Array<{ type: 'plus' | 'minus'; text: string }> = [
      ...argumentPool.plus.map(text => ({ type: 'plus' as const, text })),
      ...argumentPool.minus.map(text => ({ type: 'minus' as const, text }))
    ];

    // Перемешиваем аргументы для разнообразия
    let shuffledArgs = [...allArguments].sort(() => Math.random() - 0.5);
    let argIndex = 0;
    
    // Функция для сброса аргументов
    const resetArguments = () => {
      shuffledArgs = [...allArguments].sort(() => Math.random() - 0.5);
      argIndex = 0;
    };

    // Функция для проверки, находится ли точка внутри формы мозга (эллипс)
    const isInsideBrain = (x: number, y: number, centerX: number, centerY: number, brainRadius: number): boolean => {
      const dx = (x - centerX) / brainRadius;
      const dy = (y - centerY) / (brainRadius * 0.75); // Эллиптическая форма
      return (dx * dx + dy * dy) <= 1;
    };

    const prepareArgumentLayout = (
      arg: Argument,
      context: CanvasRenderingContext2D,
      baseSize: number
    ) => {
      if (!arg.text) {
        arg.lines = [];
        arg.cardWidth = 0;
        arg.cardHeight = 0;
        arg.fontSize = 0;
        arg.lineHeight = 0;
        arg.padding = 0;
        arg.layoutBaseSize = baseSize;
        return;
      }

      const padding = 12;
      const maxWidth = Math.min(180, baseSize * 0.3);
      const fontSize = Math.max(12, baseSize * 0.025);
      const lineHeight = fontSize * 1.4;
      context.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;

      const words = arg.text.split(' ');
      const lines: string[] = [];
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const metrics = context.measureText(testLine);
        if (metrics.width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }

      if (currentLine) {
        lines.push(currentLine);
      }

      const widestLine = lines.reduce(
        (max, line) => Math.max(max, context.measureText(line).width),
        0
      );

      arg.lines = lines;
      arg.cardWidth = widestLine + padding * 2;
      arg.cardHeight = lines.length * lineHeight + padding * 2;
      arg.fontSize = fontSize;
      arg.lineHeight = lineHeight;
      arg.padding = padding;
      arg.layoutBaseSize = baseSize;
    };

    // Функция для создания нового аргумента внутри мозга
    const createArgument = (baseSize: number): Argument => {
      const centerX = displayWidth / 2;
      const centerY = displayHeight / 2;
      const brainRadius = Math.min(displayWidth, displayHeight) * 0.4; // Радиус мозга
      
      // Генерируем позицию внутри мозга (эллиптическая форма)
      let x, y;
      let attempts = 0;
      do {
        const angle = Math.random() * 2 * Math.PI;
        const distance = Math.random() * brainRadius * 0.9; // Не до самого края
        x = centerX + Math.cos(angle) * distance;
        y = centerY + Math.sin(angle) * distance * 0.75;
        attempts++;
      } while (!isInsideBrain(x, y, centerX, centerY, brainRadius) && attempts < 10);

      // Берем следующий аргумент из списка
      const argData = shuffledArgs[argIndex % shuffledArgs.length];
      argIndex++;

      // Начальная скорость для роения (медленное движение)
      const speed = 0.3 + Math.random() * 0.4;
      const angle = Math.random() * 2 * Math.PI;

      const newArgument: Argument = {
        id: Date.now() + Math.random(),
        x,
        y,
        type: argData.type,
        text: argData.text,
        opacity: 0.1,
        scale: 0.8 + Math.random() * 0.3,
        rotation: (Math.random() - 0.5) * 15,
        rotationSpeed: (Math.random() - 0.5) * 0.5,
        velocityX: Math.cos(angle) * speed,
        velocityY: Math.sin(angle) * speed * 0.75, // Учитываем эллиптическую форму
        lines: [],
        cardWidth: 0,
        cardHeight: 0,
        fontSize: 0,
        lineHeight: 0,
        padding: 0,
        layoutBaseSize: baseSize
      };

      prepareArgumentLayout(newArgument, ctx, baseSize);

      return newArgument;
    };

    // Функция отрисовки аргумента (текстовые карточки)
    const drawArgument = (arg: Argument, baseSize: number) => {
      if (!arg.text) return; // Пропускаем если нет текста

      if (Math.abs(arg.layoutBaseSize - baseSize) > 20) {
        prepareArgumentLayout(arg, ctx, baseSize);
      }
      
      ctx.save();
      ctx.translate(arg.x, arg.y);
      ctx.rotate((arg.rotation * Math.PI) / 180);
      ctx.scale(arg.scale, arg.scale);
      ctx.globalAlpha = arg.opacity;

      const { cardWidth, cardHeight, lines, lineHeight, fontSize } = arg;

      ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Цвета в зависимости от типа аргумента (без рамок, только фон)
      const isPositive = arg.type === 'plus';
      const bgColor = isPositive 
        ? 'rgba(34, 197, 94, 0.7)' 
        : 'rgba(239, 68, 68, 0.7)';
      const textColor = 'rgba(255, 255, 255, 1)';
      
      // Рисуем карточку с закругленными углами (без рамки)
      const cornerRadius = 8;
      const x = -cardWidth / 2;
      const y = -cardHeight / 2;
      
      // Функция для рисования закругленного прямоугольника
      const drawRoundedRect = (x: number, y: number, width: number, height: number, radius: number) => {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
      };
      
      // Фон карточки (без обводки)
      ctx.fillStyle = bgColor;
      drawRoundedRect(x, y, cardWidth, cardHeight, cornerRadius);
      ctx.fill();
      
      // Рисуем текст
      ctx.fillStyle = textColor;
      lines.forEach((line, index) => {
        const yPos = (index - (lines.length - 1) / 2) * lineHeight;
        ctx.fillText(line, 0, yPos);
      });

      ctx.restore();
    };

    // Функция для создания статичного финального состояния (все карточки разбросаны)
    const renderStaticFinalState = () => {
      // Убеждаемся, что canvas правильно инициализирован
      resizeCanvas();
      
      const baseSize = Math.min(displayWidth, displayHeight);
      const centerX = displayWidth / 2;
      const centerY = displayHeight / 2;
      const brainRadius = Math.min(displayWidth, displayHeight) * 0.4;

      // Создаём все аргументы сразу
      const staticArgs: Argument[] = [];
      const shuffledArgs = [...allArguments].sort(() => Math.random() - 0.5);
      
      console.log('[AnimatedBrain] renderStaticFinalState: создаём', allArguments.length, 'аргументов');

      for (let i = 0; i < allArguments.length; i++) {
        const argData = shuffledArgs[i % shuffledArgs.length];
        
        // Генерируем позицию внутри мозга (эллиптическая форма)
        let x, y;
        let attempts = 0;
        do {
          const angle = Math.random() * 2 * Math.PI;
          const distance = Math.random() * brainRadius * 0.9; // Не до самого края
          x = centerX + Math.cos(angle) * distance;
          y = centerY + Math.sin(angle) * distance * 0.75;
          attempts++;
        } while (!isInsideBrain(x, y, centerX, centerY, brainRadius) && attempts < 10);

        const staticArg: Argument = {
          id: Date.now() + Math.random() + i,
          x,
          y,
          type: argData.type,
          text: argData.text,
          opacity: 0.95, // Полная непрозрачность
          scale: 0.8 + Math.random() * 0.3, // Случайный масштаб
          rotation: (Math.random() - 0.5) * 15, // Случайный поворот
          rotationSpeed: 0, // Без вращения
          velocityX: 0, // Без движения
          velocityY: 0,
          lines: [],
          cardWidth: 0,
          cardHeight: 0,
          fontSize: 0,
          lineHeight: 0,
          padding: 0,
          layoutBaseSize: baseSize
        };

        prepareArgumentLayout(staticArg, ctx, baseSize);
        staticArgs.push(staticArg);
      }

      // Очищаем canvas и отрисовываем все карточки один раз
      ctx.clearRect(0, 0, displayWidth, displayHeight);
      
      console.log('[AnimatedBrain] renderStaticFinalState: отрисовываем', staticArgs.length, 'карточек');
      
      for (const arg of staticArgs) {
        drawArgument(arg, baseSize);
      }

      // Устанавливаем флаг, чтобы больше не пытаться анимировать
      argumentsRef.current = staticArgs; // Сохраняем для возможного использования
      
      console.log('[AnimatedBrain] renderStaticFinalState: отрисовка завершена');
    };

    // Обработчик изменения размера - перерисовываем статичное состояние если нужно
    const handleResize = () => {
      resizeCanvas();
      // Если анимация отключена из-за производительности или принудительно через URL, перерисовываем статичное состояние
      if (isPerformanceDisabledRef.current || forceStaticMode) {
        renderStaticFinalState();
      }
    };
    
    // Если принудительно включен статичный режим через URL параметр
    if (forceStaticMode) {
      console.log('[AnimatedBrain] Инициализация статичного режима...');
      renderStaticFinalState(); // Сначала отрисовываем
      isPerformanceDisabledRef.current = true; // Затем устанавливаем флаг
      console.log('[AnimatedBrain] Статичное состояние отрисовано');
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
    
    window.addEventListener('resize', handleResize);

    // Функция анимации
    const animate = () => {
      if (!isAnimatingRef.current || isPerformanceDisabledRef.current) {
        animationRef.current = undefined;
        return;
      }

      // Обновляем размеры если нужно
      const rect = container.getBoundingClientRect();
      if (rect.width !== displayWidth || rect.height !== displayHeight) {
        resizeCanvas();
      }

      ctx.clearRect(0, 0, displayWidth, displayHeight);

      const args = argumentsRef.current;
      const baseSize = Math.min(displayWidth, displayHeight);

      // Добавляем новые аргументы постепенно (заполнение мозга)
      if (fillProgressRef.current < 1) {
        fillProgressRef.current += 0.005; // Скорость заполнения (немного медленнее для эффекта)
        
        // Используем все аргументы из списка, но добавляем постепенно
        const totalArgs = allArguments.length;
        const targetCount = Math.floor(fillProgressRef.current * totalArgs);
        while (args.length < targetCount && args.length < totalArgs) {
          args.push(createArgument(baseSize));
        }
      }

      // Центр и радиус мозга для проверки границ
      const centerX = displayWidth / 2;
      const centerY = displayHeight / 2;
      const brainRadius = Math.min(displayWidth, displayHeight) * 0.4;

      // Анимируем аргументы (роение внутри мозга)
      for (let i = 0; i < args.length; i++) {
        const arg = args[i];
        
        // Плавное появление
        if (arg.opacity < 0.95) {
          arg.opacity = Math.min(0.95, arg.opacity + 0.05);
        }

        // Очень медленное вращение для читаемости текста
        arg.rotation += arg.rotationSpeed * 0.3;
        
        // Легкая пульсация
        const pulse = Math.sin(Date.now() * 0.001 + i * 0.3) * 0.05 + 1;
        const baseScale = 0.85 + (i % 3) * 0.1;
        arg.scale = baseScale * pulse;

        // Движение (роение) - аргументы движутся внутри мозга
        arg.x += arg.velocityX;
        arg.y += arg.velocityY;

        // Проверка границ мозга и отскок
        const dx = (arg.x - centerX) / brainRadius;
        const dy = (arg.y - centerY) / (brainRadius * 0.75);
        const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

        if (distanceFromCenter > 0.9) {
          // Отскок от границы мозга
          const normalX = dx / distanceFromCenter;
          const normalY = dy / distanceFromCenter;
          
          // Отражаем скорость
          const dot = arg.velocityX * normalX + arg.velocityY * normalY;
          arg.velocityX -= 2 * dot * normalX;
          arg.velocityY -= 2 * dot * normalY;
          
          // Немного замедляем при отскоке
          arg.velocityX *= 0.8;
          arg.velocityY *= 0.8;
          
          // Возвращаем аргумент внутрь мозга
          arg.x = centerX + normalX * brainRadius * 0.85;
          arg.y = centerY + normalY * brainRadius * 0.75 * 0.85;
        }

        // Случайные изменения направления для более хаотичного движения
        if (Math.random() < 0.02) {
          const newAngle = Math.random() * 2 * Math.PI;
          const speed = Math.sqrt(arg.velocityX * arg.velocityX + arg.velocityY * arg.velocityY);
          arg.velocityX = Math.cos(newAngle) * speed;
          arg.velocityY = Math.sin(newAngle) * speed * 0.75;
        }

        // Небольшое затухание скорости для плавности
        arg.velocityX *= 0.999;
        arg.velocityY *= 0.999;

        drawArgument(arg, baseSize);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    // Обработчик низкой производительности
    const handleLowPerformance = (avgFps: number) => {
      console.log(`[AnimatedBrain] Низкая производительность обнаружена: ${avgFps.toFixed(1)} FPS. Отключаем анимацию и показываем статичное состояние.`);
      
      // Останавливаем анимацию
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
      
      isAnimatingRef.current = false;
      
      // Останавливаем мониторинг
      if (performanceMonitorRef.current) {
        performanceMonitorRef.current.stop();
        performanceMonitorRef.current = null;
      }
      
      // Отрисовываем статичное финальное состояние
      renderStaticFinalState();
    };

    // Intersection Observer для оптимизации
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const shouldAnimate = entry.isIntersecting && !isPerformanceDisabledRef.current;
          isAnimatingRef.current = shouldAnimate;
          
          if (shouldAnimate && !animationRef.current) {
            // Сбрасываем прогресс при первом появлении (только если не отключено из-за производительности)
            if (!isPerformanceDisabledRef.current) {
              fillProgressRef.current = 0;
              argumentsRef.current = [];
              resetArguments();
              
              // Запускаем мониторинг производительности только для обычного canvas (не для Worker)
              if (!performanceMonitorRef.current && !supportsOffscreenCanvas) {
                const monitor = createPerformanceMonitor();
                performanceMonitorRef.current = monitor;
                monitor.start({
                  threshold: 20, // FPS ниже 20
                  duration: 3000, // в течение 3 секунд
                  onLowPerformance: handleLowPerformance,
                  debug: debugFpsMode,
                  debugLabel: 'AnimatedBrain'
                });
              }
              
              animate();
            }
          } else if (!shouldAnimate && animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = undefined;
            
            // Останавливаем мониторинг когда элемент не виден
            if (performanceMonitorRef.current) {
              performanceMonitorRef.current.stop();
              performanceMonitorRef.current = null;
            }
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(container);

    // Запускаем анимацию сразу, если элемент уже виден
    const checkVisibility = () => {
      const rect = container.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      if (isVisible && !animationRef.current && !isPerformanceDisabledRef.current) {
        isAnimatingRef.current = true;
        resetArguments();
        
        // Запускаем мониторинг производительности только для обычного canvas (не для Worker)
        if (!performanceMonitorRef.current && !supportsOffscreenCanvas) {
          const monitor = createPerformanceMonitor();
          performanceMonitorRef.current = monitor;
          monitor.start({
            threshold: 20, // FPS ниже 20
            duration: 3000, // в течение 3 секунд
            onLowPerformance: handleLowPerformance,
            debug: debugFpsMode,
            debugLabel: 'AnimatedBrain'
          });
        }
        
        animate();
      }
    };

    // Проверяем видимость сразу и через небольшую задержку
    checkVisibility();
    const timeoutId = setTimeout(checkVisibility, 100);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      isAnimatingRef.current = false;
      
      // Останавливаем мониторинг производительности
      if (performanceMonitorRef.current) {
        performanceMonitorRef.current.stop();
        performanceMonitorRef.current = null;
      }
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
    };
  }, [size, argumentPool]);

  return (
    <div 
      ref={containerRef}
      className={`relative w-full h-full flex items-center justify-center ${containerClassName}`}
    >
      {/* SVG иконка мозга на фоне (без ограничений) */}
      <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 0, pointerEvents: 'none' }}>
        <Brain 
          className="w-full h-full text-purple-400"
          style={{ 
            opacity: 0.2,
            width: '100%',
            height: '100%'
          }}
        />
      </div>
      
      {/* Canvas с анимацией аргументов */}
      <canvas
        ref={canvasRef}
        className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
        style={{ zIndex: 1 }}
      />
    </div>
  );
};

export default AnimatedBrain;

